import { useCallback } from 'react';
import { useForm } from 'react-hook-form';

import * as templateApi from 'api/templateApi';
import { REGISTRATION_STEP } from 'constants/registrationStep';
import { useToast } from 'hooks/useToast';
import { useAppDispatch, useAppSelector } from 'store/hooks/useStore';
import {
  registrationActions,
  registrationSelectors,
} from 'store/reducers/apiKeyTemplateReducer/registrationSlice';
import { loaderActions } from 'store/reducers/loaderReducer/loaderSlice';
import { ApiKeyTemplate } from 'types/apiKeyTemplate';
import { EditHookReturnType } from '../types';
import { useListStore } from './useListStore';

/**
 * コンポーネント登録 store の実装をコンポーネントから隠すための中間層
 */
export const useRegistrationStore = (): EditHookReturnType => {
  const dispatch = useAppDispatch();
  const { fetchApiKeyTemplates } = useListStore();
  const toast = useToast();
  const form = useForm<ApiKeyTemplate>({
    mode: 'all',
    defaultValues: {
      name: '',
      template: '',
    },
  });

  const { getValues, reset, handleSubmit } = form;

  const currentModalStep = useAppSelector(
    registrationSelectors.currentModalStep,
  );

  const isOpenModal = useAppSelector(registrationSelectors.isOpenModal);

  const onOpenModal = useCallback(() => {
    dispatch(registrationActions.modalOpened());
  }, [dispatch]);

  const onCloseModal = () => {
    dispatch(registrationActions.initialize());
    reset();
  };

  const onCloseModalWhenCompleted = () => {
    onCloseModal();
    void fetchApiKeyTemplates();
  };

  const onConfirm = () => {
    dispatch(registrationActions.updateStep(REGISTRATION_STEP.CONFIRMATION));
  };

  const onGoBack = () => {
    dispatch(registrationActions.updateStep(REGISTRATION_STEP.INPUT));
  };

  const onSubmit = handleSubmit(
    async (values: ApiKeyTemplate) => {
      dispatch(loaderActions.setLoading(true));
      const result = await templateApi.addApiKeyTemplate(values);
      if (result.ok) {
        dispatch(registrationActions.updateStep(REGISTRATION_STEP.RESULT));
      } else {
        toast({
          status: 'error',
          description: `APIキーテンプレートの登録に失敗しました: ${result.err.message}`,
        });
      }
      dispatch(loaderActions.setLoading(false));
    },
    () => {
      toast({
        status: 'error',
        description: 'APIキーテンプレートの登録に失敗しました。',
      });
    },
  );

  return {
    mode: '登録',
    isOpenModal,
    currentModalStep,
    onOpenModal,
    onCloseModal,
    onCloseModalWhenCompleted,
    onConfirm,
    onGoBack,
    onSubmit,
    onOpenModalDelete: () => null,
    form,
    values: getValues(),
    clawlers: undefined,
  };
};
